// Config panel for the "http_request" action block
import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Paper,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const HttpRequestConfig = ({ config, onChange, onSummaryChange }) => {
  const [jsonError, setJsonError] = useState(null);

  const headers = config.headers || [{ key: '', value: '' }];

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    const method = (updated.method || 'POST').toUpperCase();
    const url = updated.url || '(no URL)';
    // Show just the hostname for summary
    try {
      const host = new URL(url).hostname;
      onSummaryChange(`${method} ${host}`);
    } catch {
      onSummaryChange(`${method} ${url.substring(0, 30)}`);
    }
  };

  const handleBodyChange = (value) => {
    // Validate JSON as user types
    if (value.trim()) {
      try {
        JSON.parse(value);
        setJsonError(null);
      } catch (e) {
        setJsonError(e.message);
      }
    } else {
      setJsonError(null);
    }
    handleChange('body', value);
  };

  const updateHeaders = (newHeaders) => {
    const updated = { ...config, headers: newHeaders };
    onChange(updated);
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = headers.map((h, i) => i === index ? { ...h, [field]: value } : h);
    updateHeaders(newHeaders);
  };

  const addHeader = () => {
    updateHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    if (headers.length <= 1) {
      updateHeaders([{ key: '', value: '' }]);
      return;
    }
    updateHeaders(headers.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Sends an HTTP request to any endpoint — internal or third-party.
        Configure the URL, method, authentication, headers, and JSON body.
      </Typography>

      {/* Method + URL */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Method</InputLabel>
          <Select
            value={config.method || 'POST'}
            onChange={(e) => handleChange('method', e.target.value)}
            label="Method"
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="URL"
          value={config.url || ''}
          onChange={(e) => handleChange('url', e.target.value)}
          fullWidth
          required
          placeholder="https://api.example.com/webhook"
          helperText="Full URL including https://"
        />
      </Box>

      {/* Authentication */}
      <Accordion variant="outlined" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            Authentication
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Auth Method</InputLabel>
            <Select
              value={config.authMethod || 'none'}
              onChange={(e) => handleChange('authMethod', e.target.value)}
              label="Auth Method"
            >
              <MenuItem value="none">No Authentication</MenuItem>
              <MenuItem value="bearer">Bearer Token</MenuItem>
              <MenuItem value="basic">Basic Auth (user:pass)</MenuItem>
              <MenuItem value="api_key_header">API Key (Header)</MenuItem>
              <MenuItem value="api_key_query">API Key (Query Param)</MenuItem>
            </Select>
            <FormHelperText>How to authenticate with the endpoint</FormHelperText>
          </FormControl>

          {config.authMethod === 'bearer' && (
            <TextField
              size="small"
              label="Bearer Token"
              value={config.authToken || ''}
              onChange={(e) => handleChange('authToken', e.target.value)}
              required
              type="password"
              helperText="Will be sent as Authorization: Bearer <token>"
            />
          )}

          {config.authMethod === 'basic' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Username"
                value={config.authUsername || ''}
                onChange={(e) => handleChange('authUsername', e.target.value)}
                required
                fullWidth
              />
              <TextField
                size="small"
                label="Password"
                value={config.authPassword || ''}
                onChange={(e) => handleChange('authPassword', e.target.value)}
                required
                type="password"
                fullWidth
              />
            </Box>
          )}

          {config.authMethod === 'api_key_header' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Header Name"
                value={config.authHeaderName || 'X-API-Key'}
                onChange={(e) => handleChange('authHeaderName', e.target.value)}
                required
                fullWidth
                helperText="e.g. X-API-Key, Authorization"
              />
              <TextField
                size="small"
                label="API Key"
                value={config.authApiKey || ''}
                onChange={(e) => handleChange('authApiKey', e.target.value)}
                required
                type="password"
                fullWidth
              />
            </Box>
          )}

          {config.authMethod === 'api_key_query' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Query Param Name"
                value={config.authQueryParam || 'api_key'}
                onChange={(e) => handleChange('authQueryParam', e.target.value)}
                required
                fullWidth
                helperText="e.g. api_key, token"
              />
              <TextField
                size="small"
                label="API Key"
                value={config.authApiKey || ''}
                onChange={(e) => handleChange('authApiKey', e.target.value)}
                required
                type="password"
                fullWidth
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Headers */}
      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            Headers
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {headers.map((header, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Header"
                value={header.key || ''}
                onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                placeholder="Content-Type"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Value"
                value={header.value || ''}
                onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                placeholder="application/json"
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => removeHeader(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addHeader}>
            Add Header
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* JSON Body */}
      <TextField
        size="small"
        label="Request Body (JSON)"
        value={config.body || ''}
        onChange={(e) => handleBodyChange(e.target.value)}
        multiline
        rows={6}
        placeholder={'{\n  "event": "automation_triggered",\n  "data": {}\n}'}
        error={!!jsonError}
        helperText={jsonError || 'JSON payload to send. Leave empty for GET requests.'}
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: '0.8rem' }
        }}
      />

      {/* Response Variable */}
      <TextField
        size="small"
        label="Store Response As Variable"
        value={config.responseVariable || ''}
        onChange={(e) => handleChange('responseVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. apiResponse"
        helperText={
          config.responseVariable
            ? `JSON response will be available as {{${config.responseVariable}.field}}, e.g. {{${config.responseVariable}.results.0.locationId}}`
            : 'Optional. Variable name to store the parsed JSON response for downstream blocks.'
        }
      />

      {/* Timeout */}
      <TextField
        size="small"
        label="Timeout (seconds)"
        type="number"
        value={config.timeout || 10}
        onChange={(e) => handleChange('timeout', parseInt(e.target.value, 10) || 10)}
        inputProps={{ min: 1, max: 60 }}
        helperText="Max wait time (1-60 seconds)"
      />
    </Box>
  );
};

export default HttpRequestConfig;
